import { ShiftManagementUpdateUnassignedShiftDocument } from 'generated/resources/graphql'
import moment from 'moment'

import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useMutation } from 'modules/core/hooks/useMutation'
import { i18nDateToString } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import buildShiftsVisibility from 'modules/shift_management/lib/buildShiftsVisibility'
import type UnassignedShiftForm from 'modules/shift_management/lib/forms/UnassignedShiftForm'
import { trackEditUnassignedShiftSubmit } from 'modules/shift_management/lib/trackEvents'
import type UnassignedShift from 'modules/shift_management/store/internal/models/UnassignedShift'
import UnassignedShiftProp from 'modules/shift_management/store/internal/models/UnassignedShift'

import { useUnassignedShifts } from './useUnassignedShifts'

export interface Props {
  unassignedShiftForm: UnassignedShiftForm
  unassignedShift: UnassignedShift
}

const trackEvent = (
  unassignedShift: UnassignedShift,
  unassignedShiftForm: UnassignedShiftForm,
  teamsIdsVisibilities: number[],
  locationsIdsVisibilities: number[]
) => {
  const shiftType = unassignedShift.isDefault ? 'defaultShift' : 'customShift'
  const counter = unassignedShiftForm.get('counter').value
  const startAt = unassignedShiftForm.get('start_at').value
  const endAt = unassignedShiftForm.get('end_at').value
  const extraHours = unassignedShiftForm.get('extra_hours').value
  const locationId = unassignedShiftForm.get('location_id').value
  const workAreaId = unassignedShiftForm.get('work_area_id').value
  const notes = unassignedShiftForm.get('notes').value
  const allCompany = unassignedShiftForm.get('all_company').value

  const extras = {
    shift_start_time: startAt,
    shift_end_time: endAt,
    overtime_flag: !!extraHours,
    location_id: locationId,
    work_area_id: workAreaId,
    notes: notes,
  }

  trackEditUnassignedShiftSubmit(
    unassignedShift,
    shiftType,
    counter,
    allCompany,
    teamsIdsVisibilities,
    locationsIdsVisibilities,
    extras
  )
}

const useEditUnassignedShifts = ({ unassignedShiftForm, unassignedShift }: Props) => {
  const [, dispatchUnassignedShifts] = useUnassignedShifts()

  const [updateUnassignedShift, { loading }] = useMutation(
    ShiftManagementUpdateUnassignedShiftDocument,
    {
      extractorFn: (data) => data.shiftManagementMutations.updateShiftmanagementUnassignedShift,
    }
  )

  const unassignedShiftModel = unassignedShift.toPayload()

  const convertToNumber = (value: string): number | null => (value ? Number(value) : null)

  const onSubmit = async (closePortal: ClosePortalFn) => {
    const startTime = unassignedShiftForm.get('start_at').value
    const endTime = unassignedShiftForm.get('end_at').value

    // Get start and end date objects
    const startAt = moment(`${unassignedShift.startAtDate} ${startTime}`)
    // Using startAtDate to avoid overnights shifts that now are not overnights.
    const endAt = moment(`${unassignedShift.startAtDate} ${endTime}`)

    // If not overnight and end hour is less than start hour, add a day to endAt
    if (startAt.isAfter(endAt)) endAt.add(1, 'days')

    const { teamsIdsVisibilities, locationsIdsVisibilities } = buildShiftsVisibility({
      form: unassignedShiftForm,
      isSingleSelector: true,
    })

    trackEvent(unassignedShift, unassignedShiftForm, teamsIdsVisibilities, locationsIdsVisibilities)

    const counter = unassignedShiftForm.get('staff_requirements').value[0].staff_count
    const jobCatalogRolesIds =
      unassignedShiftForm.get('staff_requirements').value[0].job_catalog_roles_ids
    const jobCatalogTreeRoleUuids =
      unassignedShiftForm.get('staff_requirements').value[0].job_catalog_tree_role_uuids

    const result = await updateUnassignedShift({
      variables: {
        id: unassignedShiftModel.id,
        startAt: i18nDateToString(startAt, 'backend_datetime_without_timezone'),
        endAt: i18nDateToString(endAt, 'backend_datetime_without_timezone'),
        locationId: convertToNumber(unassignedShiftForm.get('location_id').value),
        workAreaId: convertToNumber(unassignedShiftForm.get('work_area_id').value),
        notes: unassignedShiftForm.get('notes').value,
        extraHours: unassignedShiftForm.get('extra_hours').value,
        counter,
        allCompany: unassignedShiftForm.get('all_company').value,
        teamsIdsVisibilities,
        locationsIdsVisibilities,
        jobCatalogRolesIds,
        jobCatalogTreeRoleUuids,
      },
    })
    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('shift_management.actions.edit_unassigned_shift_success'),
      })

      closePortal()

      const value = handled.data.unassignedShift

      if (value) {
        const unassignedShiftResponse = UnassignedShiftProp.buildFromGraphQL(value)

        dispatchUnassignedShifts({
          type: 'EDIT_UNASSIGNED_SHIFT',
          unassignedShift: unassignedShiftResponse,
        })
      }
    }
  }

  return {
    submitting: loading,
    onSubmit,
  }
}

export { useEditUnassignedShifts }
